import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';

import { auth, isAnonymousFirebaseUser } from '../firebaseHelper';
import AuthFlowLayout from '../components/AuthFlowLayout';
import PhoneSigninPanel from '../components/PhoneSigninPanel';
import PickedHighlightRow from '../components/PickedHighlightRow';
import SavingPicks from '../components/SavingPicks';
import { signinUser } from '../redux/actions/userActions';
import { setNextPreviewsInfo } from '../redux/actions/dataActions';
import { GET_STARTED_NEW_USER_RESPONSE } from '../constants/local_storage_constants';
import { playableChatPreviews } from '../utils/chatPreviewQueue';
import { clearOnboardingSession, getOnboardingPlayback, getUpcomingOnboardingHighlights, markOnboardingSignedIn, readOnboardingSession, saveOnboardingHomePicks, updateOnboardingSession } from '../utils/onboardingSession';
import './signin_new_user.css';
import { onboardingSignupTrigger, readOnboardingFlow, trackOnboardingError, trackOnboardingEvent } from '../utils/onboardingFunnel';
import { getOnboardingSelectedSpeakers } from '../utils/onboardingSelectedSpeakers';
import { runOnboardingSaveRequest } from '../utils/onboardingSaveRequest';
import { scrollAppTo } from '../utils/appViewport';

const parseGetStartedNewUserResponse = () => {
    try {
        const data = JSON.parse(localStorage.getItem(GET_STARTED_NEW_USER_RESPONSE));
        return data && typeof data.newId === 'string' && data.newId.trim() && Array.isArray(data.previews)
            ? data : null;
    } catch {
        localStorage.removeItem(GET_STARTED_NEW_USER_RESPONSE);
        return null;
    }
};

const starterAnalytics = (data, sample) => ({
    ...(data?.rosterVersion ? { roster_version: data.rosterVersion } : {}),
    ...(sample?.id ? { sample_id: sample.id } : {}),
    ...(sample?.person?.id ? { speaker_id: sample.person.id } : {}),
    ...(sample?.starterTopic ? { topic: sample.starterTopic } : {}),
    ...(Number.isFinite(sample?.starterScore) ? { starter_score: sample.starterScore } : {}),
});

export const SigninNewUser = ({ signinUser, setNextPreviewsInfo }) => {
    const navigate = useNavigate();
    const [session] = useState(readOnboardingSession);
    const [newUserData] = useState(() => session
        ? {
            newId: session.newId, previews: session.previews, sampleId: session.sample.id,
            rosterVersion: session.rosterVersion, selectedPairs: session.selectedPairs,
            selectedSpeakerIds: session.selectedSpeakerIds, selectedSpeakers: session.selectedSpeakers,
            saveUid: session.saveUid,
        }
        : parseGetStartedNewUserResponse());
    const [phoneStage, setPhoneStage] = useState('phone');
    const [phoneBusy, setPhoneBusy] = useState(false);
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [signinPending, setSigninPending] = useState(false);
    const [signinError, setSigninError] = useState(null);
    const [saveWait, setSaveWait] = useState('saving');
    const [saveAttempt, setSaveAttempt] = useState(0);
    const phonePanelRef = useRef(null);
    const migrationInFlightRef = useRef(false);
    const migrationCompleteRef = useRef(false);
    const mountedRef = useRef(false);
    const signupOpenLoggedRef = useRef(false);
    const leftForHomeRef = useRef(false);
    const automaticSigninUidRef = useRef(null);
    const readyPreviews = useMemo(() => playableChatPreviews(newUserData?.previews || []), [newUserData]);
    const selectedSpeakers = useMemo(() => getOnboardingSelectedSpeakers(newUserData || {}), [newUserData]);
    const returningToPreview = session?.reason === 'action';
    const sample = session?.sample
        || readyPreviews.find(preview => preview.id === newUserData?.sampleId)
        || readyPreviews[0];
    const upcoming = useMemo(() => getUpcomingOnboardingHighlights(readyPreviews, sample?.id, session?.heardPreviewIds || []), [readyPreviews, sample?.id, session?.heardPreviewIds]);

    useEffect(() => {
        mountedRef.current = true;
        logEvent(getAnalytics(), 'screen_view', { firebase_screen: 'signin_new_user' });
        if (!newUserData) navigate('/home', { replace: true });
        else {
            document.getElementById('audio')?.pause();
            if (!signupOpenLoggedRef.current) {
                signupOpenLoggedRef.current = true;
                trackOnboardingEvent('onboarding_signup_open', { signup_trigger: onboardingSignupTrigger(session || {}) });
            }
        }
        return () => { mountedRef.current = false; };
    }, [navigate, newUserData, sample, session]);

    useEffect(() => {
        scrollAppTo({ top: 0, behavior: 'auto' });
    }, [phoneStage]);

    useEffect(() => {
        if (!signinPending || signinError) return undefined;
        const delayed = setTimeout(() => setSaveWait('delayed'), 10000);
        const timedOut = setTimeout(() => setSaveWait('timeout'), 30000);
        return () => { clearTimeout(delayed); clearTimeout(timedOut); };
    }, [saveAttempt, signinError, signinPending]);

    const returnToPreview = () => {
        if (!sample) {
            navigate('/home', { replace: true });
            return;
        }
        updateOnboardingSession({ phase: 'preview', reason: null, action: null });
        setNextPreviewsInfo(false, [sample], { initialPreviewId: sample.id });
        navigate('/previews', { replace: true, state: { onboarding: true } });
    };

    const completeSignin = useCallback(async (user) => {
        if (!user || isAnonymousFirebaseUser(user) || !newUserData?.newId
            || migrationCompleteRef.current) return;
        const pendingSession = readOnboardingSession();
        const ownerUid = (pendingSession?.newId === newUserData.newId ? pendingSession.saveUid : null) || newUserData.saveUid;
        if (ownerUid && ownerUid !== user.uid) {
            setSigninError(Object.assign(new Error('Use your original account to save these picks.'), { code: 'onboarding/account-changed' }));
            return;
        }
        setSigninPending(true);
        setSigninError(null);
        setSaveWait('saving');
        setSaveAttempt(attempt => attempt + 1);
        if (migrationInFlightRef.current) return;
        migrationInFlightRef.current = true;
        const flowId = readOnboardingFlow()?.flowId || null;
        try {
            updateOnboardingSession({ savePending: true, saveUid: user.uid }, { newId: newUserData.newId });
            const storedGuest = parseGetStartedNewUserResponse();
            if (storedGuest?.newId === newUserData.newId) {
                localStorage.setItem(GET_STARTED_NEW_USER_RESPONSE, JSON.stringify({ ...storedGuest, saveUid: user.uid }));
            }
            const startingSession = readOnboardingSession();
            const signupSession = startingSession?.newId === newUserData.newId ? startingSession : session;
            const outcome = await runOnboardingSaveRequest(`${user.uid}:${newUserData.newId}`, async () => {
                const idToken = await user.getIdToken();
                if (auth.currentUser?.uid !== user.uid) return null;
                const currentSession = readOnboardingSession();
                const guestSession = currentSession?.newId === newUserData.newId ? currentSession : signupSession;
                const result = guestSession?.guestMigrated
                    ? await signinUser(idToken, user.refreshToken, {}, { skipGuestMigration: true })
                    : await signinUser(idToken, user.refreshToken, {
                        newId: newUserData.newId,
                        onboardingPlayback: getOnboardingPlayback(guestSession || null),
                    });
                return { result, guestSession };
            });
            if (!outcome) return;
            const { result, guestSession } = outcome;
            if (!result || result.success !== true) throw result?.error || new Error('Unable to save your picks.');
            // A user can leave while /login finishes. Persist the successful
            // account handoff even then, without changing their current route.
            if (auth.currentUser?.uid !== user.uid) return;
            migrationCompleteRef.current = true;
            if (!outcome.persisted) {
                outcome.persisted = true;
                if (!guestSession?.guestMigrated) {
                    const eligibleIds = Array.isArray(result.data?.onboardingPreviewIds)
                        ? new Set(result.data.onboardingPreviewIds) : null;
                    saveOnboardingHomePicks({
                        uid: result.data?.uid || user.uid,
                        previews: eligibleIds ? upcoming.filter(preview => eligibleIds.has(preview.id)) : upcoming,
                        rosterVersion: newUserData.rosterVersion, selectedPairs: newUserData.selectedPairs,
                    });
                }
                logEvent(getAnalytics(), 'signin_new_user', starterAnalytics(newUserData, sample));
                trackOnboardingEvent('onboarding_ready', {}, { flowId });
                try {
                    if (parseGetStartedNewUserResponse()?.newId === newUserData.newId) {
                        localStorage.removeItem(GET_STARTED_NEW_USER_RESPONSE);
                    }
                } catch {
                    // The save succeeded. A retained record is still account-owned.
                }
            }
            if (!mountedRef.current || leftForHomeRef.current) {
                if (readOnboardingSession()?.newId === newUserData.newId) markOnboardingSignedIn();
                return;
            }
            if (returningToPreview && sample) {
                markOnboardingSignedIn();
                setNextPreviewsInfo(true, [sample], { initialPreviewId: sample.id });
                navigate('/previews', { replace: true, state: { onboarding: true } });
            } else {
                clearOnboardingSession();
                navigate('/home', { replace: true, state: { scrollToTop: true } });
            }
        } catch (error) {
            trackOnboardingError('bootstrap', error, { flowId });
            if (mountedRef.current) setSigninError(error);
        } finally {
            migrationInFlightRef.current = false;
            if (mountedRef.current) setSigninPending(false);
        }
    }, [navigate, newUserData, returningToPreview, sample, session, setNextPreviewsInfo, signinUser, upcoming]);

    useEffect(() => {
        if (!newUserData) return undefined;
        return auth.onAuthStateChanged(user => {
            if (user && !isAnonymousFirebaseUser(user)) {
                setFirebaseUser(user);
                if (automaticSigninUidRef.current !== user.uid) {
                    automaticSigninUidRef.current = user.uid;
                    completeSignin(user);
                }
            }
        });
    }, [completeSignin, newUserData]);

    if (!newUserData) return null;
    const saving = signinPending || Boolean(signinError);
    const saveErrorMessage = signinError?.code === 'onboarding/account-changed' ? 'Use your original account to save these picks.'
        : signinError && navigator.onLine === false ? 'You’re offline. Check your connection.' : null;
    const goHome = () => {
        leftForHomeRef.current = true;
        navigate('/home', { replace: true });
    };
    const title = saving ? null
        : phoneStage === 'code' ? 'Enter your code' : returningToPreview ? 'Sign in to continue' : 'Keep discovering.';
    return <AuthFlowLayout className={`onboardingFlow${saving ? ' onboardingSavingPage' : ''}`} title={title} showHeader={!saving}
        onBack={!saving && !phoneBusy
            ? phoneStage === 'code' ? () => phonePanelRef.current?.changeNumber() : returnToPreview
            : undefined}
        backLabel={phoneStage === 'code' ? 'Change phone number' : sample ? 'Back to highlight' : 'Back to home'}>
        <section aria-label='Phone signup'>
            {!saving && phoneStage !== 'code' && <>
                <p className='authFlowSubtitle'>{returningToPreview
                    ? session?.action === 'full_episode' ? 'Create an account to explore the full episode.' : 'Create an account to continue. Your place is saved.'
                    : 'Sign in to save your picks, hear more highlights, and explore full episodes.'}</p>
            </>}
            {saving && <SavingPicks speakers={selectedSpeakers} error={signinError} delayed={saveWait === 'delayed'}
                timedOut={!signinError && saveWait === 'timeout'}
                errorMessage={saveErrorMessage} onHome={goHome}
                onRetry={() => completeSignin(firebaseUser || auth.currentUser)} />}
            <div className='authFlowPhonePanel' hidden={saving}>
                <PhoneSigninPanel ref={phonePanelRef} autoFocusPhone hasBackAction onStageChange={setPhoneStage} onBusyChange={setPhoneBusy} />
            </div>
            {!returningToPreview && !saving && phoneStage !== 'code' && upcoming.length > 0
                && <div className='onboardingUpcoming'>
                    <h2 className='onboardingUpcomingTitle'>Up next after signing in</h2>
                    <ul className='onboardingUpcomingHighlights' aria-label='More from your picks'>
                        {upcoming.map(preview => <PickedHighlightRow key={preview.id} preview={preview} />)}
                    </ul>
                </div>}
        </section>
    </AuthFlowLayout>;
};

SigninNewUser.propTypes = {
    signinUser: PropTypes.func.isRequired,
    setNextPreviewsInfo: PropTypes.func.isRequired,
};
export default connect(null, { signinUser, setNextPreviewsInfo })(SigninNewUser);
